'use client';
import {
  ArrowRightIcon,
  Bot,
  Users,
  TrendingUp,
  Activity,
} from "lucide-react";
import React from "react";
import { motion } from "motion/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from '@/components/ui/card';
import { THome_Config } from "@/app/config";



const featureCards = [
  {
    icon: Bot,
   
  },
  {
    icon: Users,
    
  },
  {
    icon: TrendingUp,
    
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};


export const ZumaForCompaniesSection = ({config}:{config:THome_Config['companies']}) => {
  const analyticsData = config.analyticsData
  
  const statsData = config.statsData
  return (
    <motion.section
      className="w-full py-12 md:py-20 lg:py bg-[#f8fafb] flex justify-center"
      id="companies"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            duration: 0.6,
            staggerChildren: 0.1
          }
        }
      }}
    >
      <div className="max-w-[1120px] w-full px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.header 
          className="flex flex-col items-center gap-4 md:gap-6 lg:gap-3.5 mb-8 md:mb-12 lg:mb-[49px]"
          variants={{
            hidden: { 
              opacity: 0, 
              y: 30,
              scale: 0.95
            },
            visible: { 
              opacity: 1, 
              y: 0,
              scale: 1,
              transition: {
                duration: 0.5,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.div
            variants={{
              hidden: { 
                opacity: 0, 
                y: 30,
                scale: 0.95
              },
              visible: { 
                opacity: 1, 
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.5,
                  ease: "easeOut"
                }
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Badge className="bg-white border-[#ffd6a7] text-frail-scale-05242098figmasitegrenadier text-xs md:text-sm lg:text-[10.3px] leading-tight lg:leading-[14px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal px-3 py-1 lg:px-2 lg:py-1">
              {config.tag}
            </Badge>
          </motion.div>

          <motion.h1 
            className="text-frail-scale-05242098figmasiteebony text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-tight lg:leading-[42px] text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal px-4 md:px-0"
            variants={{
              hidden: { 
                opacity: 0, 
                y: 30,
                scale: 0.95
              },
              visible: { 
                opacity: 1, 
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.5,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.title}
          </motion.h1>

          <motion.div 
            className="max-w-2xl w-full px-4 md:px-0"
            variants={{
              hidden: { 
                opacity: 0, 
                y: 30,
                scale: 0.95
              },
              visible: { 
                opacity: 1, 
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.5,
                  ease: "easeOut"
                }
              }
            }}
          >
            <p className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-sm md:text-base lg:text-[17.2px] text-center leading-relaxed lg:leading-[24.5px]">
              {config.description}
            </p>
          </motion.div>
        </motion.header>

        {/* Main Content Grid */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 md:gap-8 lg:gap-7 mb-8 md:mb-12"
          variants={containerVariants}
        >
          {/* Analytics Card */}
          <motion.div
            variants={ {
              hidden: { 
                opacity: 0, 
                x: -20,
                rotateY: -15
              },
              visible: { 
                opacity: 1, 
                x: 0,
                rotateY: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut"
                }
              },
              hover: {
                scale: 1.02,
                rotateY: 2,
                transition: {
                  duration: 0.2,
                  ease: "easeInOut"
                }
              }
            }}
            whileHover="hover"
          >
            <Card className="flex flex-col justify-center border-[#ffd6a7] bg-[linear-gradient(151deg,rgba(243,244,246,1)_0%,rgba(255,237,212,0.5)_100%)] rounded-xl lg:rounded-[12.75px]">
              <CardContent className="p-6 md:p-8 lg:p-7">
                {/* Card Header */}
                <motion.div 
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8 lg:mb-7"
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      y: 30,
                      scale: 0.95
                    },
                    visible: { 
                      opacity: 1, 
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <h2 className="text-frail-scale-05242098figmasiteebony text-lg md:text-xl lg:text-[21px] leading-tight lg:leading-7 [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal">
                    Recruitment Analytics
                  </h2>

                  <motion.div 
                    className="flex items-center gap-2 lg:gap-[7px]"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.7, 1]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Activity className="w-4 h-4 lg:w-[17.5px] lg:h-[17.5px] text-[#f44900]" />
                    </motion.div>
                    <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#f44900] text-xs md:text-sm lg:text-[12.3px] leading-tight lg:leading-[17.5px]">
                      Live Data
                    </span>
                  </motion.div>
                </motion.div>

                {/* Pipeline Card */}
                <motion.div
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      y: 30,
                      scale: 0.95
                    },
                    visible: { 
                      opacity: 1, 
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <Card className="bg-white border-[#ffecd4] rounded-lg lg:rounded-[8.75px] mb-6 md:mb-8 lg:mb-7">
                    <CardContent className="p-4 md:p-6 lg:p-[22px]">
                      <motion.div 
                        className="flex items-center justify-between mb-4 lg:mb-3.5"
                        variants={{
                          hidden: { 
                            opacity: 0, 
                            y: 30,
                            scale: 0.95
                          },
                          visible: { 
                            opacity: 1, 
                            y: 0,
                            scale: 1,
                            transition: {
                              duration: 0.5,
                              ease: "easeOut"
                            }
                          }
                        }}
                      >
                        <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-sm md:text-base lg:text-[13.9px] leading-tight lg:leading-[21px]">
                          Hiring Pipeline
                        </span>
                        <motion.span 
                          className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#00a63d] text-sm md:text-base lg:text-[13.7px] leading-tight lg:leading-[21px]"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1, duration: 0.5 }}
                        >
                          +23% this month
                        </motion.span>
                      </motion.div>

                      <motion.div 
                        className="space-y-4 lg:space-y-[10.5px]"
                        variants={containerVariants}
                      >
                        {/* Applications Progress */}
                        <motion.div variants={{
                              hidden: { 
                                opacity: 0, 
                                y: 30,
                                scale: 0.95
                              },
                              visible: { 
                                opacity: 1, 
                                y: 0,
                                scale: 1,
                                transition: {
                                  duration: 0.5,
                                  ease: "easeOut"
                                }
                              }
                            }}>
                          <div className="flex items-center justify-between">
                            <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm md:text-base lg:text-[13.9px] leading-tight lg:leading-[21px]">
                              Applications
                            </span>
                            <motion.span 
                              className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#f44900] text-sm md:text-base leading-tight lg:leading-[21px]"
                              variants={{
                                hidden: { scale: 0.8, opacity: 0 },
                                visible: {
                                  scale: 1,
                                  opacity: 1,
                                  transition: {
                                    duration: 0.6,
                                    ease: "backOut",
                                    delay: 0.3
                                  }
                                }
                              }}
                            >
                              {analyticsData.applications.count}
                            </motion.span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 lg:h-[7px] overflow-hidden">
                            <motion.div
                              className={`h-2 lg:h-[7px] ${analyticsData.applications.color} rounded-full`}
                              variants={{
                                hidden: { width: 0 },
                                visible: (progress: number) => ({
                                  width: `${progress}%`,
                                  transition: {
                                    duration: 1.5,
                                    ease: "easeOut",
                                    delay: 0.5
                                  }
                                })
                              }}
                              custom={analyticsData.applications.progress}
                              initial="hidden"
                              whileInView="visible"
                              viewport={{ once: true }}
                            />
                          </div>
                        </motion.div>

                        {/* Qualified Candidates Progress */}
                        <motion.div variants={{
                              hidden: { 
                                opacity: 0, 
                                y: 30,
                                scale: 0.95
                              },
                              visible: { 
                                opacity: 1, 
                                y: 0,
                                scale: 1,
                                transition: {
                                  duration: 0.5,
                                  ease: "easeOut"
                                }
                              }
                            }}>
                          <div className="flex items-center justify-between">
                            <span className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-frail-scale-05242098figmasiteebony text-sm md:text-base leading-tight lg:leading-[21px]">
                              Qualified Candidates
                            </span>
                            <motion.span 
                              className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#155cfb] text-sm md:text-base leading-tight lg:leading-[21px]"
                              variants={{
                                hidden: { scale: 0.8, opacity: 0 },
                                visible: {
                                  scale: 1,
                                  opacity: 1,
                                  transition: {
                                    duration: 0.6,
                                    ease: "backOut",
                                    delay: 0.3
                                  }
                                }
                              }}
                            >
                              {analyticsData.qualified.count}
                            </motion.span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 lg:h-[7px] overflow-hidden">
                            <motion.div
                              className={`h-2 lg:h-[7px] ${analyticsData.qualified.color} rounded-full`}
                              variants={{
                                hidden: { width: 0 },
                                visible: (progress: number) => ({
                                  width: `${progress}%`,
                                  transition: {
                                    duration: 1.5,
                                    ease: "easeOut",
                                    delay: 0.5
                                  }
                                })
                              }}
                              custom={analyticsData.qualified.progress}
                              initial="hidden"
                              whileInView="visible"
                              viewport={{ once: true }}
                            />
                          </div>
                        </motion.div>
                      </motion.div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Stats Grid */}
                <motion.div 
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-3.5"
                  variants={containerVariants}
                >
                  {statsData.map((stat, index) => (
                    <motion.div 
                      key={index} 
                      className="text-center"
                      variants={{
                        hidden: { 
                          opacity: 0, 
                          y: 30,
                          scale: 0.95
                        },
                        visible: { 
                          opacity: 1, 
                          y: 0,
                          scale: 1,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <motion.div
                        className={`${stat.color} text-xl md:text-2xl lg:text-[21px] leading-tight lg:leading-7 [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal`}
                        variants={{
                          hidden: { scale: 0.8, opacity: 0 },
                          visible: {
                            scale: 1,
                            opacity: 1,
                            transition: {
                              duration: 0.6,
                              ease: "backOut",
                              delay: 0.3
                            }
                          }
                        }}
                      >
                        {stat.value}
                      </motion.div>
                      <div className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-xs md:text-sm lg:text-[12.3px] leading-tight lg:leading-[17.5px] mt-1">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Feature Cards */}
          <motion.div 
            className="flex flex-col gap-4 md:gap-6 lg:gap-[21px]"
            variants={containerVariants}
          >
            {config.featureCards.map((card, index) => {
              const Icon = featureCards[index].icon
              return(
              <motion.div
                key={index}
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    x: -20,
                    rotateY: -15
                  },
                  visible: { 
                    opacity: 1, 
                    x: 0,
                    rotateY: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    scale: 1.02,
                    rotateY: 2,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut"
                    }
                  }
                }}
                whileHover="hover"
                custom={index}
              >
                <Card className="bg-white border rounded-xl lg:rounded-[12.75px]">
                  <CardContent className="p-4 md:p-6 lg:p-[21px]">
                    <motion.div
                      className={`w-10 h-10 md:w-12 md:h-12 lg:w-[42px] lg:h-[42px] ${card.bgColor} rounded-lg lg:rounded-[8.75px] flex items-center justify-center mb-3 md:mb-4 lg:mb-[13px]`}
                      whileHover={{ 
                        rotate: 360,
                        scale: 1.1,
                        transition: { duration: 0.6 }
                      }}
                    >
                      <Icon
                        className={`w-5 h-5 md:w-6 md:h-6 lg:w-[21px] lg:h-[21px] ${card.iconColor}`}
                      />
                    </motion.div>

                    <motion.h3 
                      className="text-frail-scale-05242098figmasiteebony text-base md:text-lg lg:text-[15.8px] leading-tight lg:leading-[24.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal mb-2 lg:mb-2"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                    >
                      {card.title}
                    </motion.h3>

                    <motion.p 
                      className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-[#495565] text-sm md:text-base lg:text-[12.1px] leading-relaxed lg:leading-[17.5px]"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                    >
                      {card.description}
                    </motion.p>
                  </CardContent>
                </Card>
              </motion.div>
            )})}
          </motion.div>
        </motion.div>

              {/* CTA Button */}
              <motion.div 
                className="flex justify-center"
                variants={{
                  hidden: { 
                    opacity: 0, 
                    y: 30,
                    scale: 0.95
                  },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut"
                    }
                  }
                }}
              >
          <motion.a 
            href={config.cta.href}
            variants={{
              hover: {
                scale: 1.05,
                boxShadow: "0 10px 25px rgba(244, 73, 0, 0.3)",
                transition: {
                  duration: 0.2,
                  ease: "easeInOut"
                }
              },
              tap: {
                scale: 0.98
              }
            }}
            whileHover="hover"
            whileTap="tap"
          >
            <Button className="bg-[#f44900] hover:bg-[#d63e00] text-white rounded-lg lg:rounded-[6.75px] h-auto px-6 py-3 md:px-8 md:py-4 lg:px-2.5 lg:py-[5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-sm md:text-base lg:text-[12.3px] leading-tight lg:leading-[17.5px]">
              {config.cta.label}
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <ArrowRightIcon className="ml-2 lg:ml-[7px] w-4 h-4 md:w-5 md:h-5 lg:w-3.5 lg:h-3.5" />
              </motion.div>
            </Button>
          </motion.a>
        </motion.div>
      </div>
    </motion.section>
  );
};